import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Willkommen im Partner Portal! 👋',
    content: 'Schön, dass du da bist! Lass uns dir kurz zeigen, wie alles funktioniert. Dieser Rundgang dauert nur 2 Minuten.',
    position: 'center'
  },
  {
    id: 'dashboard',
    title: 'Dein Dashboard',
    content: 'Hier siehst du alle wichtigen Kennzahlen auf einen Blick: Aktuelle Fälle, Provisionslevel und offene Rückfragen.',
    target: '.dashboard-stats',
    position: 'bottom'
  },
  {
    id: 'new-case',
    title: 'Neuer Fall',
    content: 'Mit diesem Button startest du einen neuen Schadenfall. Der Prozess ist in einfache Schritte unterteilt.',
    target: '.new-case-button',
    position: 'bottom'
  },
  {
    id: 'cases-list',
    title: 'Deine Fälle',
    content: 'Hier findest du alle deine Fälle. Klicke auf "Akte öffnen" um Details zu sehen oder auf "Details" für den Status.',
    target: '#cases-list',
    position: 'top'
  },
  {
    id: 'process',
    title: 'Der Ablauf',
    content: 'Jeder Fall durchläuft diese Schritte:\n1. Grunddaten erfassen\n2. Fotos hochladen\n3. Video aufnehmen\n4. Dokumente unterschreiben\n5. Fall absenden',
    position: 'center'
  },
  {
    id: 'provisions',
    title: 'Provisionen',
    content: 'Unter "Provisionen" siehst du alle abgerechneten Fälle und deine Auszahlungen. Je mehr Fälle, desto höher dein Provisionslevel!',
    target: '.provisions-link',
    position: 'bottom'
  },
  {
    id: 'finish',
    title: 'Bereit zum Start! 🚀',
    content: 'Das war\'s! Du kannst diesen Rundgang jederzeit über dein Profil neu starten. Viel Erfolg mit deinen ersten Fällen!',
    position: 'center'
  }
];

const OnboardingTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(true);

  const step = TOUR_STEPS[currentStep];

  useEffect(() => {
    if (step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        let top = 0;
        let left = 0;

        switch (step.position) {
          case 'bottom':
            top = rect.bottom + scrollY + 20;
            left = rect.left + scrollX + rect.width / 2;
            break;
          case 'top':
            top = rect.top + scrollY - 20;
            left = rect.left + scrollX + rect.width / 2;
            break;
          default:
            top = window.innerHeight / 2 + scrollY;
            left = window.innerWidth / 2;
        }

        setPosition({ top, left });

        // Highlight element
        element.style.position = 'relative';
        element.style.zIndex = '9999';
        element.style.boxShadow = '0 0 0 4px rgba(34, 197, 94, 0.5)';
        element.style.borderRadius = '12px';

        // Scroll into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        return () => {
          element.style.position = '';
          element.style.zIndex = '';
          element.style.boxShadow = '';
        };
      }
    }
  }, [currentStep, step]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    setIsVisible(false);
    if (onComplete) onComplete(true); // true = skipped
  };

  if (!isVisible) return null;

  const isCenter = step.position === 'center' || !step.target;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 z-[9998]" onClick={handleSkip}></div>

      {/* Tour Tooltip */}
      <div
        className={`fixed z-[9999] bg-white rounded-2xl shadow-2xl border-2 border-brand-green/30 p-6 max-w-md ${
          isCenter ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' : ''
        }`}
        style={
          !isCenter
            ? {
                top: `${position.top}px`,
                left: `${position.left}px`,
                transform: step.position === 'bottom' ? 'translateX(-50%)' : 'translate(-50%, -100%)'
              }
            : {}
        }
      >
        {/* Close/Skip Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
          <p className="text-gray-700 text-sm whitespace-pre-line">{step.content}</p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            {TOUR_STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`h-1 flex-1 rounded-full ${
                  idx <= currentStep ? 'bg-brand-green' : 'bg-gray-200'
                }`}
              ></div>
            ))}
          </div>
          <p className="text-xs text-gray-500 text-center">
            Schritt {currentStep + 1} von {TOUR_STEPS.length}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3">
          {currentStep > 0 ? (
            <button 
              onClick={handlePrev}
              className="px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </button>
          ) : (
            <button 
              onClick={handleSkip}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
            >
              Später ansehen
            </button>
          )}

          <button
            onClick={handleNext}
            className="px-6 py-2 bg-brand-green hover:bg-brand-green-dark text-white font-semibold rounded-lg transition-colors inline-flex items-center gap-2"
          >
            {currentStep === TOUR_STEPS.length - 1 ? 'Fertig' : 'Weiter'}
            {currentStep < TOUR_STEPS.length - 1 && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </>
  );
};

export default OnboardingTour;
